import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { broadcastTabSync } from "../../lib/tabSync";
import { defaultWsUrl, type ConnectionState, StyxClient } from "../../lib/styxClient";
import { startTabLeader } from "../../lib/tabLeader";
import { getStoredJwt } from "../../lib/authApi";
import type { StyxFrame } from "../../lib/frame";
import { routeFrame, type FrameRouterDeps } from "../realtime/frameRouter";
import { relayReducer, initialRelayState, type RelayAction } from "../state/relayState";
import { useSessionResetRegistry } from "../SessionResetContext";
import { useAuth } from "./AuthContext";
import { useContacts } from "./ContactsContext";

type RealtimeContextValue = {
  conn: ReturnType<typeof initialRelayState>["conn"];
  authenticated: boolean;
  isWsLeader: boolean;
  isWsLeaderRef: React.MutableRefObject<boolean>;
  clientRef: React.MutableRefObject<StyxClient | null>;
  sendRealtimeCommand: (type: string, payload: Record<string, unknown>, id?: string) => boolean;
  dispatchRelay: React.Dispatch<RelayAction>;
  registerFrameDeps: (deps: FrameRouterDeps | null) => void;
  routeIncomingFrame: (frame: StyxFrame) => void;
  authenticatedRef: React.MutableRefObject<boolean>;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { register } = useSessionResetRegistry();
  const { identityRow, keys, setBoot } = useAuth();
  const { dispatchContacts } = useContacts();
  const [relay, dispatchRelay] = useReducer(relayReducer, undefined, initialRelayState);
  const [isWsLeader, setIsWsLeader] = useState(true);
  const clientRef = useRef<StyxClient | null>(null);
  const prevConnRef = useRef<ConnectionState | null>(null);
  const authenticatedRef = useRef(false);
  const isWsLeaderRef = useRef(isWsLeader);
  const frameDepsRef = useRef<FrameRouterDeps | null>(null);

  isWsLeaderRef.current = isWsLeader;

  useEffect(() => {
    authenticatedRef.current = relay.authenticated;
  }, [relay.authenticated]);

  const registerFrameDeps = useCallback((deps: FrameRouterDeps | null) => {
    frameDepsRef.current = deps;
  }, []);

  const routeIncomingFrame = useCallback((frame: StyxFrame) => {
    void routeFrame(frame, frameDepsRef.current);
  }, []);

  useEffect(() => {
    return register(() => {
      clientRef.current?.disconnect();
      clientRef.current = null;
      dispatchRelay({ type: "reset" });
    });
  }, [register]);

  const sendRealtimeCommand = useCallback(
    (type: string, payload: Record<string, unknown>, id?: string): boolean => {
      if (isWsLeader) {
        return clientRef.current?.send(type, payload, id) ?? false;
      }
      if (relay.conn !== "open") return false;
      broadcastTabSync({ type: "ws-command", frameType: type, payload, id });
      return true;
    },
    [isWsLeader, relay.conn],
  );

  const handleFrameRef = useRef<(frame: StyxFrame) => void>(() => {});
  handleFrameRef.current = (frame: StyxFrame) => {
    routeIncomingFrame(frame);
  };

  useEffect(() => {
    if (!identityRow || !keys || !getStoredJwt()) return;
    return startTabLeader((leader) => {
      setIsWsLeader(leader);
      if (!leader) {
        setBoot("relay", "active", "following browser leader");
      }
    });
  }, [identityRow?.userId, keys?.userId, setBoot]);

  useEffect(() => {
    if (!identityRow || !keys || !getStoredJwt() || !isWsLeader) return;
    broadcastTabSync({
      type: "ws-state",
      conn: relay.conn,
      authenticated: relay.authenticated,
      leader: true,
    });
  }, [identityRow, isWsLeader, keys, relay.authenticated, relay.conn]);

  useEffect(() => {
    if (!identityRow || !keys || !getStoredJwt()) return;
    if (!isWsLeader) {
      clientRef.current?.disconnect();
      clientRef.current = null;
      return;
    }

    setBoot("relay", "active", "dialing websocket");
    dispatchRelay({ type: "set-authenticated", authenticated: false });

    const client = new StyxClient(defaultWsUrl());
    clientRef.current = client;

    const unsubscribeFrame = client.onFrame((frame) => {
      if (frame.type !== "auth.challenge" && frame.type !== "auth.ok") {
        broadcastTabSync({ type: "ws-frame", frame });
      }
      void handleFrameRef.current(frame);
    });
    const unsubscribeState = client.onState((conn) => {
      const prev = prevConnRef.current;
      prevConnRef.current = conn;
      dispatchRelay({ type: "set-conn", conn });

      if (conn === "connecting") setBoot("relay", "active", "dialing websocket");
      if (conn === "open") setBoot("relay", "done", "socket open");
      if (prev === "open" && conn !== "open") {
        dispatchContacts({ type: "reset-presence" });
      }
      if (conn === "closed") {
        if (!authenticatedRef.current) setBoot("relay", "active", "retry scheduled");
      }
    });

    client.connect();

    return () => {
      prevConnRef.current = null;
      unsubscribeFrame();
      unsubscribeState();
      client.disconnect();
      clientRef.current = null;
    };
  }, [identityRow?.userId, isWsLeader, keys?.userId, setBoot, dispatchContacts]);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      conn: relay.conn,
      authenticated: relay.authenticated,
      isWsLeader,
      isWsLeaderRef,
      clientRef,
      sendRealtimeCommand,
      dispatchRelay,
      registerFrameDeps,
      routeIncomingFrame,
      authenticatedRef,
    }),
    [relay.authenticated, relay.conn, isWsLeader, sendRealtimeCommand, registerFrameDeps, routeIncomingFrame],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error("useRealtime outside RealtimeProvider");
  return ctx;
}
